// Extracted from BatucadaRio.jsx — KontaktEditor

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Download, Settings, ArrowLeft,
  Drum, Clock, SkipBack, Repeat, Layers, ChevronLeft, ChevronRight,
  Sliders, Grid3X3, FileAudio, Code, Package, FolderUp, Save, ChevronUp, ChevronDown,
  Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';

import { WaveEditorTab } from './WaveEditorTab';
import { InstrumentOptionsTab } from './InstrumentOptionsTab';
import { GroupEditorTab } from './GroupEditorTab';
import { MappingEditorTab } from './MappingEditorTab';
import { ScriptEditorTab } from './ScriptEditorTab';

// ============================================
// KONTAKT-STYLE EDITOR WITH 5 TABS (Collapsible)
// ============================================
export const KontaktEditor = ({ 
  selectedKey, 
  keyMappings, 
  keyBuffers, 
  keySettings,
  onTrimChange, 
  onClose,
  activeTab,
  onTabChange,
  bpm,
  isCollapsed,
  onToggleCollapse,
  onSavePreset,
  onClearKey,
  onClearAllKeys,
  onDeleteSample,
  onRemapKey,
  groupSettings,
  onGroupSettingsChange
}) => {
  const tabs = [
    { id: 'wave', label: 'Wave Editor', icon: FileAudio },
    { id: 'instrument', label: 'Instrument', icon: Sliders },
    { id: 'group', label: 'Group Editor', icon: Layers },
    { id: 'mapping', label: 'Mapping Editor', icon: Grid3X3 },
    { id: 'script', label: 'Script Editor', icon: Code },
  ];

  const sample = selectedKey ? keyMappings[selectedKey] : null;
  const audioBuffer = selectedKey ? keyBuffers[selectedKey] : null;
  const settings = selectedKey ? keySettings[selectedKey] : null;
  const mappedKeysCount = Object.keys(keyMappings).length;

  // Collapsed state - just show a thin bar
  if (isCollapsed) {
    return (
      <div 
        className="h-8 bg-[#1a1a1a] border-t border-[#444] flex items-center justify-between px-3 cursor-pointer hover:bg-[#222]"
        onClick={onToggleCollapse}
      >
        <span className="text-[10px] text-gray-500">
          {selectedKey ? `Editing: ${sample?.name || selectedKey}` : 'Editor (collapsed)'}
        </span>
        <ChevronUp className="w-4 h-4 text-gray-500" />
      </div>
    );
  }

  if (!selectedKey) {
    return (
      <div className="bg-[#1a1a1a] border-t border-[#444]">
        <div 
          className="h-8 flex items-center justify-between px-3 border-b border-[#333] cursor-pointer hover:bg-[#222]"
          onClick={onToggleCollapse}
        >
          <span className="text-[10px] text-gray-500">Editor</span>
          <div className="flex items-center gap-2">
            {mappedKeysCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); onClearAllKeys?.(); }}
                className="px-2 py-0.5 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                title="Clear all key mappings"
              >
                Clear All ({mappedKeysCount})
              </button>
            )}
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </div>
        </div>
        <div className="h-32 flex items-center justify-center">
          <p className="text-gray-500 text-sm">Click a mapped key to edit</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1a1a] border-t border-[#444]">
      {/* Tab Bar with collapse button */}
      <div className="flex border-b border-[#333] bg-[#222]">
        <button
          onClick={onToggleCollapse}
          className="px-2 py-2 text-gray-500 hover:text-white hover:bg-[#333] border-r border-[#333]"
          title="Collapse editor"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all border-b-2
                ${isActive 
                  ? 'text-amber-400 border-amber-400 bg-[#2a2a2a]' 
                  : 'text-gray-500 border-transparent hover:text-gray-300 hover:bg-[#2a2a2a]'}`}
              data-testid={`editor-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
        <div className="flex-1" />
        
        {/* Clear This Key */}
        <button
          onClick={() => onClearKey?.(selectedKey)}
          className="px-3 py-2 text-gray-400 hover:text-white text-xs flex items-center gap-1 hover:bg-[#333]"
          title="Unmap this key (doesn't delete sample)"
        >
          <X className="w-3 h-3" />
          Clear Key
        </button>
        
        {/* Delete Sample */}
        <button
          onClick={() => onDeleteSample?.(selectedKey, sample)}
          className="px-3 py-2 text-red-400 hover:text-red-300 text-xs flex items-center gap-1 hover:bg-red-500/10"
          title="Delete sample from database"
        >
          <Trash2 className="w-3 h-3" />
          Delete
        </button>
        
        {/* Save Preset */}
        <button
          onClick={onSavePreset}
          className="px-3 py-2 text-amber-400 hover:text-amber-300 text-xs flex items-center gap-1"
          title="Save current mappings as preset"
        >
          <Save className="w-3 h-3" />
          Save
        </button>
        
        <button
          onClick={onClose}
          className="px-3 py-2 text-gray-500 hover:text-white text-sm"
        >
          Close
        </button>
      </div>

      {/* Tab Content */}
      <div className="p-3">
        {activeTab === 'wave' && (
          <WaveEditorTab 
            sample={sample} 
            audioBuffer={audioBuffer} 
            settings={settings}
            selectedKey={selectedKey}
            onTrimChange={onTrimChange}
            bpm={bpm}
          />
        )}
        {activeTab === 'instrument' && (
          <InstrumentOptionsTab 
            sample={sample} 
            selectedKey={selectedKey} 
            settings={settings}
            onSettingsChange={onTrimChange}
          />
        )}
        {activeTab === 'group' && (
          <GroupEditorTab
            sample={sample}
            selectedKey={selectedKey}
            settings={settings}
            keyMappings={keyMappings}
            keySettings={keySettings}
            onTrimChange={onTrimChange}
            groupSettings={groupSettings}
            onGroupSettingsChange={onGroupSettingsChange}
          />
        )}
        {activeTab === 'mapping' && (
          <MappingEditorTab 
            sample={sample} 
            selectedKey={selectedKey}
            keyMappings={keyMappings}
            keyBuffers={keyBuffers}
            keySettings={keySettings}
            onSettingsChange={onTrimChange}
            onRemapKey={onRemapKey}
          />
        )}
        {activeTab === 'script' && (
          <ScriptEditorTab sample={sample} selectedKey={selectedKey} settings={settings} onTrimChange={onTrimChange} />
        )}
      </div>
    </div>
  );
};
